import * as React from 'react'

import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { format, parseISO } from 'date-fns'
import {
  Calendar as CalendarIcon,
  CalendarClock,
  Clock,
  DollarSign,
  Layers,
  Link as LinkIcon,
  MapPin,
  Medal,
  Pencil,
  Text,
  Trash2,
  Users,
} from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'

import { EditEventDialog } from '@/features/calendar/components/dialogs/add-event-dialog'
import { useDeleteEvent, useRegisterForEvent } from '@/utils/api/membership-service'
import { useCalendar } from '@/features/calendar/contexts/use-calendar'
import { handleError } from '@/lib/utils'

interface EventDetailsDialogProps {
  children: React.ReactNode
  training: ICalendarEvent
}

export const EventDetailsDialog = React.forwardRef<
  React.ComponentRef<typeof DialogTrigger>,
  EventDetailsDialogProps
>(({ children, training }, ref) => {
  const { canManageEvents, currentMember } = useCalendar()
  const [open, setOpen] = React.useState(false)
  const queryClient = useQueryClient()
  const deleteMutation = useDeleteEvent()
  const registerMutation = useRegisterForEvent(
    {
      mutation: {
        onError: (e: import('axios').AxiosError) => handleError(e, 'Failed to register for the event. Please try again.'),
      }
    }
  )

  const startDate = safeParseDate(training.startAt)
  const endDate = safeParseDate(training.endAt)

  const eventId = React.useMemo(() => {
    if (typeof training.sourceId === 'number' && Number.isFinite(training.sourceId)) {
      return training.sourceId
    }
    const parsed = Number(training.id)
    return Number.isFinite(parsed) ? parsed : undefined
  }, [training.id, training.sourceId])

  const categoryColor = React.useMemo(() => {
    switch (training.category) {
      case 'training':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'webinar':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'networking':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }, [training.category])

  const registrations = typeof training.registrations === 'number' ? training.registrations : undefined
  const spotsRemaining = typeof training.availableSpots === 'number' ? Math.max(training.availableSpots, 0) : undefined
  const locationLabel = training.isOnline ? 'Online' : training.location || 'Location to be confirmed'

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      deleteMutation.reset()
      registerMutation.reset()
    }
  }

  const handleDelete = async () => {
    if (typeof eventId === 'undefined') {
      toast.error('Unable to delete the event because its identifier is missing.')
      return
    }

    try {
      await deleteMutation.mutateAsync({ id: eventId })
      await queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] })
      toast.success('Event deleted successfully.')
      setOpen(false)
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Unable to delete the event. Please try again.'))
    }
  }

  const canManage = canManageEvents && typeof eventId === 'number'
  const canApply = !canManageEvents
  const isRegistering = registerMutation.isPending
  const isFull = typeof spotsRemaining === 'number' && spotsRemaining <= 0

  const handleApply = async () => {
    if (isRegistering) return
    if (typeof eventId === 'undefined') {
      toast.error('Unable to register because the event identifier is missing.')
      return
    }

    const memberName = currentMember?.name?.trim()
    const memberEmail = currentMember?.email?.trim()
    const memberPhone = currentMember?.phone?.trim()

    // Allow registration with just a name if email is not available
    if (!memberName && !memberEmail) {
      toast.error('Please update your profile with your name before applying for CPD events.')
      return
    }

    const resolvedName = memberName || memberEmail || 'Attendee'

      await registerMutation.mutateAsync({
        eventId,
        params: {
          memberName: resolvedName,
          memberEmail: memberEmail || '',
          memberPhone,
        },
      })

      await queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] })
      toast.success('You have successfully applied for this CPD event.')
      setOpen(false)
    
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger ref={ref} asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" aria-describedby="event-details-dialog">
        <DialogHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <DialogTitle className="text-left text-xl font-semibold leading-tight">{training.title}</DialogTitle>
            <Badge className={categoryColor}>{training.category}</Badge>
          </div>
          {training.status && (
            <DialogDescription>
              Event status:{' '}
              <span className="font-medium text-foreground">{training.status}</span>
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <DetailItem icon={CalendarIcon} label="Starts">
            {format(startDate, "EEEE, MMM d, yyyy 'at' h:mm a")}
          </DetailItem>

          <DetailItem icon={Clock} label="Ends">
            {format(endDate, "EEEE, MMM d, yyyy 'at' h:mm a")}
          </DetailItem>

          <DetailItem icon={MapPin} label="Location">
            {locationLabel}
          </DetailItem>

          {training.theme && (
            <DetailItem icon={Layers} label="Theme">
              {training.theme}
            </DetailItem>
          )}

          {typeof registrations === 'number' && (
            <DetailItem icon={Users} label="Registrations">
              {registrations}
            </DetailItem>
          )}

          {typeof spotsRemaining === 'number' && spotsRemaining >= 0 && (
            <DetailItem icon={Users} label="Spots remaining">
              {spotsRemaining}
            </DetailItem>
          )}

          {typeof training.points === 'number' && (
            <DetailItem icon={Medal} label="CPD points awarded">
              {training.points}
            </DetailItem>
          )}

          {training.registrationDeadline && (
            <DetailItem icon={CalendarClock} label="Registration closes">
              {format(safeParseDate(training.registrationDeadline), "EEE, MMM d yyyy 'at' h:mm a")}
            </DetailItem>
          )}

          {typeof training.cost === 'number' && (
            <DetailItem icon={DollarSign} label="Cost">
              UGX {Intl.NumberFormat('en-UG', { maximumFractionDigits: 0 }).format(training.cost)}
            </DetailItem>
          )}

          {training.onlineLink && (
            <DetailItem icon={LinkIcon} label="Online meeting link">
              <a
                href={training.onlineLink}
                target="_blank"
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                Join session
              </a>
            </DetailItem>
          )}

          {training.facilitators && (
            <DetailItem icon={Users} label="Facilitators">
              {training.facilitators}
            </DetailItem>
          )}

          {training.description && (
            <DetailItem icon={Text} label="Description">
              {training.description}
            </DetailItem>
          )}
        </div>

        {canManage && (
          <div className="flex flex-wrap items-center justify-end gap-2 pt-4">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={deleteMutation.isPending}
                >
                  <Trash2 className="mr-2 size-4" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this event?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. The event will be removed from the calendar and members
                    will no longer see it listed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={deleteMutation.isPending}>
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    disabled={deleteMutation.isPending}
                    onClick={handleDelete}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Delete event'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <EditEventDialog event={training}>
              <Button
                variant="secondary"
                size="sm"
                disabled={deleteMutation.isPending}
              >
                <Pencil className="mr-2 size-4" />
                Edit event
              </Button>
            </EditEventDialog>
          </div>
        )}
        {!canManage && canApply && (
          <div className="flex flex-wrap items-center justify-end gap-2 pt-4">
            <Button
              size="sm"
              onClick={handleApply}
              disabled={isRegistering || isFull || typeof eventId === 'undefined'}
            >
              {isFull ? 'Event full' : isRegistering ? 'Applying…' : 'Apply for CPD'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
})

EventDetailsDialog.displayName = 'EventDetailsDialog'

function DetailItem({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-1 size-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="text-sm font-medium">{label}</p>
        <div className="text-sm text-muted-foreground">{children}</div>
      </div>
    </div>
  )
}

function safeParseDate(value: string | undefined): Date {
  if (!value) return new Date()
  const parsed = parseISO(value)
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const maybeError = error as { message?: unknown; response?: unknown }

    if (maybeError.response && typeof maybeError.response === 'object') {
      const response = maybeError.response as { data?: unknown }
      if (response.data && typeof response.data === 'object') {
        const data = response.data as Record<string, unknown>
        if (typeof data.message === 'string' && data.message.trim()) {
          return data.message
        }
        if (typeof data.error === 'string' && data.error.trim()) {
          return data.error
        }
      }
    }

    if (typeof maybeError.message === 'string' && maybeError.message.trim()) {
      return maybeError.message
    }
  }

  return fallback
}
